"use client";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@kkeut/lib/firebase/client";

/**
 * i18n 값 해석: 객체면 locale → ko → 첫 유효값, 문자열이면 그대로.
 */
function pickI18n(v, locale = "ko") {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    const l = String(locale || "ko").toLowerCase();
    const map = {};
    for (const [k, val] of Object.entries(v)) map[String(k).toLowerCase()] = val;
    const found =
      map[l] ?? map.ko ?? Object.values(map).find((x) => x != null && String(x).trim() !== "");
    return found != null ? String(found) : "";
  }
  return "";
}

/**
 * 페이지 안내 문구를 Firestore에서 동적으로 읽는다.
 * 경로: {branchId}/pageNotices/pages/{page}
 * 필드: text/linkLabel (문자열 또는 {ko,en,ja,zh,th} 객체) / linkUrl(문자열, 공통)
 *
 * @param {string} branchId - 지점 ID
 * @param {string} [page="reserve"] - 페이지 키
 * @param {string} [locale="ko"] - 현재 언어 (객체일 때 해당 언어로 해석)
 * @returns {{text:string, linkUrl:string, linkLabel:string} | null}
 *   text 있으면 해석된 문자열 객체, 없으면 null → 호출부에서 하드코딩 fallback
 */
export function usePageNotice(branchId, page = "reserve", locale = "ko") {
  const [raw, setRaw] = useState(null);

  useEffect(() => {
    if (!branchId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, branchId, "pageNotices", "pages", page));
        if (!cancelled) setRaw(snap.exists() ? snap.data() : null);
      } catch {
        if (!cancelled) setRaw(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, page]);

  if (!raw) return null;
  const text = pickI18n(raw.text, locale).trim();
  if (!text) return null;
  return {
    text,
    linkUrl: typeof raw.linkUrl === "string" ? raw.linkUrl.trim() : "",
    linkLabel: pickI18n(raw.linkLabel, locale).trim(),
  };
}
